/**
 * Telling the SFU a producer is gone.
 *
 * `producer.close()` on mediasoup-client is local. Nothing crosses the wire, so
 * unless the app says so the server keeps the producer — and keeps handing it to
 * anyone who asks what is in the room. `readyToConsume` does exactly that on
 * every join, so a producer the server still holds is replayed to the next
 * person through the door.
 *
 * That is how a stopped screen share came back. Stopping it closed the local
 * producer, told the room over signalling (which is why everybody already in the
 * meeting saw it go), and asked the owning node to free it with a fire-and-forget
 * `fetch(..., { method: 'DELETE' }).catch(() => {})`. When that call did not land
 * — a stale `sfuNodeUrl`, a node that moved, any transient failure, all of them
 * swallowed by the empty catch — the server kept the producer with nothing said.
 * Live participants never noticed, because they were told over a different
 * channel. Only someone who refreshed found out, because a rejoin asks the SFU
 * rather than the room, and got a screen share nobody was sharing.
 *
 * ## Why the control socket first
 *
 * `SfuControl.closeProducer` has existed all along and was called from nowhere.
 * It is the better channel in three ways: it rides the socket the client is
 * already known on rather than a URL it has to guess, it answers, so a failure
 * is a failure rather than a dropped promise, and the server-side handler
 * broadcasts `producerClosed` to the room — so the room hears it from the SFU
 * instead of only from a signalling event the SFU knows nothing about.
 *
 * The HTTP route stays as the fallback. It is the only channel available if the
 * control adapter is gone, which is precisely when a producer is most likely to
 * be orphaned.
 *
 * Neither channel is allowed to throw at the caller. Teardown runs while the
 * user is leaving, unmounting or switching devices, and a rejection there takes
 * out the rest of the cleanup. The outcome is RETURNED instead, so the caller
 * can log a real failure rather than discarding it.
 */

export type ProducerCloseChannels = {
  /** The control socket every other operation already uses. Tried first. */
  control?: { closeProducer(producerId: string): Promise<void> } | null
  /** Direct call to the owning node. Tried only if the control channel cannot. */
  http?: ((producerId: string) => Promise<{ ok: boolean; status: number }>) | null
}

export type ProducerCloseOutcome =
  | { via: 'control' }
  | { via: 'http' }
  /** Nothing got through. `reason` is for the log, not for the user. */
  | { via: 'none'; reason: string }

const describe = (err: unknown): string =>
  err instanceof Error ? err.message : String(err)

export async function closeRemoteProducer(
  producerId: string,
  { control, http }: ProducerCloseChannels,
): Promise<ProducerCloseOutcome> {
  const reasons: string[] = []

  if (control) {
    try {
      await control.closeProducer(producerId)
      return { via: 'control' }
    } catch (err) {
      // Deliberately falls through rather than returning. A control socket that
      // just rejected is the case the HTTP route exists for.
      reasons.push(`control: ${describe(err)}`)
    }
  } else {
    reasons.push('control: unavailable')
  }

  if (http) {
    try {
      const res = await http(producerId)
      // A 404 means the server does not have it, which is the state being asked
      // for. Treating that as a failure would log noise on every double-close.
      if (res.ok || res.status === 404) return { via: 'http' }
      reasons.push(`http: ${res.status}`)
    } catch (err) {
      reasons.push(`http: ${describe(err)}`)
    }
  } else {
    reasons.push('http: unavailable')
  }

  return { via: 'none', reason: reasons.join('; ') }
}
